import { Icon, type IconName } from '../Icon.js';
import { MoreMenu } from '../ui/MoreMenu.js';

export type AddMode = 'LINK' | 'UPLOAD' | 'NOTE';

export const ADD_MODE_LABELS: Readonly<Record<AddMode, string>> = Object.freeze({
  LINK: 'Link',
  UPLOAD: 'Upload files',
  NOTE: 'Note',
});

/**
 * "ADD REFERENCE ▾": the button opens the add drawer, the caret picks which of
 * its three modes to open on. Both halves are one control; only the page's ONE
 * primary context draws it red (`primary`), as `btn-primary` on the main half.
 */
export function AddReferenceButton({
  label,
  primary,
  onAdd,
  testId,
}: {
  label: string;
  primary: boolean;
  onAdd: (mode: AddMode) => void;
  testId: string;
}) {
  return (
    <div className="ref-split" data-primary={primary ? '1' : '0'} data-testid={testId}>
      <button
        type="button"
        className={primary ? 'btn btn-primary ref-split-main' : 'btn ref-split-main'}
        data-testid={`${testId}-main`}
        onClick={() => {
          onAdd('LINK');
        }}
      >
        <Icon name="plus" size={16} strokeWidth={2.2} />
        {label}
      </button>
      <MoreMenu
        label="Choose what to add"
        buttonClassName="btn ref-split-caret"
        buttonIcon="chevronDown"
        testId={`${testId}-mode`}
        items={(['LINK', 'UPLOAD', 'NOTE'] as const).map((mode) => ({
          id: mode,
          label: mode === 'LINK' ? 'Add a link' : mode === 'UPLOAD' ? 'Upload files' : 'Write a note',
          testId: `${testId}-mode-${mode}`,
          onSelect: () => {
            onAdd(mode);
          },
        }))}
      />
    </div>
  );
}

/**
 * THE EMPTY LIBRARY (approved mockup, 2026-11-02): the illustration, what the
 * library is for, the page's one red button, and four ways in.
 */
export function EmptyLibrary({
  canEdit,
  refusal,
  onAdd,
  onOrganize,
}: {
  canEdit: boolean;
  refusal: string | null;
  onAdd: (mode: AddMode) => void;
  onOrganize: () => void;
}) {
  const tiles: readonly { id: string; icon: IconName; title: string; body: string; run: () => void }[] = [
    { id: 'links', icon: 'link', title: 'Add links', body: 'Videos, articles, online galleries', run: () => { onAdd('LINK'); } },
    { id: 'files', icon: 'image', title: 'Upload files', body: 'Images, videos, PDFs, documents', run: () => { onAdd('UPLOAD'); } },
    { id: 'notes', icon: 'note', title: 'Add notes', body: 'Ideas, research, creative notes', run: () => { onAdd('NOTE'); } },
    { id: 'groups', icon: 'layers', title: 'Organize by groups', body: 'Group references by mood, locations, scenes and more', run: onOrganize },
  ];

  return (
    <div className="ref-empty-library" data-testid="ref-empty-library">
      <EmptyIllustration />
      <h3 className="ref-empty-title">No references yet</h3>
      <p className="ref-empty-body">
        Start building a shared collection of inspiration, locations, scene ideas and other materials for your
        production.
      </p>
      {canEdit ? (
        <AddReferenceButton label="Add your first reference" primary onAdd={onAdd} testId="ref-empty-add" />
      ) : (
        <p className="ref-quiet" data-testid="ref-empty-refusal">
          {refusal ?? 'You can read this library but not add to it.'}
        </p>
      )}
      {canEdit ? (
        <ul className="ref-empty-tiles">
          {tiles.map((t) => (
            <li key={t.id}>
              <button type="button" className="ref-empty-tile" data-testid={`ref-empty-tile-${t.id}`} onClick={t.run}>
                <span className="ref-empty-tile-icon">
                  <Icon name={t.icon} size={20} />
                </span>
                <span className="ref-empty-tile-text">
                  <strong>{t.title}</strong>
                  <span>{t.body}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** A video still, a landscape photograph and a PDF, fanned — drawn inline, no image request. */
function EmptyIllustration() {
  return (
    <svg
      className="ref-empty-art"
      viewBox="0 0 320 190"
      role="img"
      aria-label="A video, a photograph and a document gathered together"
      data-testid="ref-empty-illustration"
    >
      <ellipse cx="166" cy="104" rx="132" ry="76" fill="#fef3f2" />
      <ellipse cx="200" cy="72" rx="62" ry="40" fill="#fee4e2" />
      {/* sparkle */}
      <g stroke="#d43f3f" strokeWidth="4" strokeLinecap="round">
        <path d="M112 34 120 48" />
        <path d="M98 52 112 58" />
        <path d="M134 22 136 36" />
      </g>
      {/* document */}
      <g transform="rotate(8 250 110)">
        <rect x="200" y="62" width="96" height="104" fill="#ffffff" stroke="#eaecf0" strokeWidth="2" />
        <rect x="214" y="104" width="64" height="5" fill="#d0d5dd" />
        <rect x="214" y="116" width="56" height="5" fill="#d0d5dd" />
        <rect x="214" y="128" width="62" height="5" fill="#d0d5dd" />
        <rect x="214" y="140" width="40" height="5" fill="#d0d5dd" />
        <rect x="258" y="70" width="26" height="24" fill="#d43f3f" />
        <path d="M264 88c4-10 6-14 8-12s-10 8-13 6 9-3 12 1" stroke="#ffffff" strokeWidth="2" fill="none" strokeLinecap="round" />
      </g>
      {/* video */}
      <g transform="rotate(-8 92 122)">
        <rect x="40" y="82" width="110" height="80" fill="#ffffff" stroke="#eaecf0" strokeWidth="2" />
        <rect x="46" y="88" width="98" height="68" fill="#98a2b3" />
        <path d="M46 156 78 118 104 140 122 124 144 146V156z" fill="#667085" />
        <circle cx="95" cy="122" r="14" fill="#ffffff" />
        <path d="M91 115v14l11-7z" fill="#667085" />
      </g>
      {/* photograph */}
      <g transform="rotate(4 168 98)">
        <rect x="110" y="40" width="118" height="116" fill="#ffffff" stroke="#eaecf0" strokeWidth="2" />
        <rect x="118" y="48" width="102" height="92" fill="#cfe3f3" />
        <path d="M118 112c20-14 34-10 50-20s30-18 52-8v56H118z" fill="#5f8f5a" />
        <path d="M118 124c26-8 46-2 70-10s24-6 32-4v30H118z" fill="#3f6f74" />
        <circle cx="200" cy="66" r="8" fill="#fdf2cc" />
      </g>
    </svg>
  );
}
